import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { TID } from "../lib/testIds";
import { CASE_LIST } from "../data/cases";

function CaseCard({ c, idx }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const isLeft = idx % 2 === 0;

  return (
    <motion.div
      ref={ref}
      initial={{ y: 30, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative block ${isLeft ? "md:col-span-7" : "md:col-span-5 md:mt-24"}`}
    >
      <Link
        to={`/work/${c.slug}`}
        data-testid={TID.work.card(c.slug)}
        className="block"
      >
        <div className="relative aspect-[4/3] md:aspect-[5/4] overflow-hidden rounded-2xl border border-hairline bg-surface-2">
          <motion.img
            src={c.image}
            alt={c.title}
            style={{ y }}
            className="absolute inset-[-10%] w-[120%] h-[120%] object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 ease-out"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-[11px] tracking-[0.25em] uppercase text-zinc-200 font-mono-x">
            <span>{c.tag}</span>
            <span>{c.year}</span>
          </div>
          <div className="absolute bottom-6 left-6 right-6">
            <div className="text-[11px] tracking-[0.25em] uppercase text-zinc-300 font-mono-x mb-2">
              {c.client}
            </div>
            <h3 className="font-display text-2xl md:text-3xl lg:text-4xl font-medium tracking-tight leading-tight text-white">
              {c.title}
            </h3>
          </div>
          <div className="absolute top-5 right-5 h-9 w-9 rounded-full bg-white/10 backdrop-blur-md border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-500 text-white">
            ↗
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

export default function Work() {
  return (
    <section
      id="work"
      data-testid={TID.work.section}
      className="relative py-28 md:py-40 bg-surface border-y border-hairline"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
          <div>
            <span className="text-[11px] tracking-[0.28em] uppercase text-muted-x font-mono-x">
              ◇ Selected Work
            </span>
            <h2 className="mt-4 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95] max-w-3xl text-primary-x">
              Recent <em className="not-italic text-muted-x">shipments.</em>
            </h2>
          </div>
          <p className="md:max-w-sm text-secondary-x leading-relaxed">
            A small selection of products we&apos;ve designed, built and operated
            with our clients over the last 18 months.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10">
          {CASE_LIST.map((c, i) => (
            <CaseCard c={c} idx={i} key={c.slug} />
          ))}
        </div>
      </div>
    </section>
  );
}
